import React, { useState, useEffect } from "react";
import { Briefcase, MapPin, Clock, ChevronRight, Heart, Smile, Sparkles, Loader2 } from "lucide-react";
import CommonButton from "../../../components/CommonButton";

import { Job } from "../../../types/Response/CustomerResponse";
import { useLazyGetActiveJobQuery } from "../../../redux/actions/Customer/FooterCustomer";
import JobApplicationModal from "./JobApplicationModal";


const THEME = {
    mainBrandColor: "#6DBE45",
    logoHeading: "#2F5E2E",
    accent: "#A8D97B",
    background: "#F4F4F2",
    mediumGreen: "#5FB63F",
};

const PERKS_DATA = [
    {
        icon: <Sparkles size={24} style={{ color: THEME.logoHeading }} />,
        title: "Growth & Innovation",
        desc: "Work on bleeding-edge web and mobile platforms with room to experiment, build, and lead projects from scratch."
    },
    {
        icon: <Heart size={24} style={{ color: THEME.logoHeading }} />,
        title: "Flexible Work Culture",
        desc: "We prioritize deep work and genuine outcomes over clock-watching. Enjoy flexible hours and hybrid/remote choices."
    },
    {
        icon: <Smile size={24} style={{ color: THEME.logoHeading }} />,
        title: "Great Vibes & Perks",
        desc: "A warm, open peer-to-peer environment with competitive health coverage, learning allowances, and fun team outings."
    }
];

const CareersPage: React.FC = () => {
    const [selectedDept, setSelectedDept] = useState("All");
    const [activeApplicationJob, setActiveApplicationJob] = useState<Job | null>(null);

    // RTK Query Hooks Setup
    const [triggerFetchJobs, { data: jobsResponse, isLoading, isError }] = useLazyGetActiveJobQuery();

    // Correctly unpack the envelope mapping (.value.data)
    const jobsData: Job[] = jobsResponse?.value?.data || [];

    useEffect(() => {
        triggerFetchJobs();
    }, [triggerFetchJobs]);

    // Extract unique departments dynamically for filter buttons
    const departments = ["All", ...Array.from(new Set(jobsData.map((job) => job.department)))];

    // Filtered list based on server database properties
    const filteredJobs = selectedDept === "All"
        ? jobsData
        : jobsData.filter((job) => job.department === selectedDept);

    return (
        <div className="min-h-screen w-full overflow-x-hidden bg-white" style={{ color: THEME.logoHeading }}>

            {/* HERO SECTION */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-16 sm:pt-16 sm:pb-20 text-center">
                <span
                    className="text-xs font-bold tracking-wider uppercase px-4 py-1.5 rounded-full mb-4 inline-block"
                    style={{ backgroundColor: `${THEME.accent}4D`, color: THEME.logoHeading }}
                >
                    We're Hiring!
                </span>
                <h1 className="text-3xl sm:text-5xl md:text-6xl font-extrabold tracking-tight max-w-4xl mx-auto leading-tight">
                    Build the future of digital experiences with us
                </h1>
                <p className="mt-4 sm:mt-6 text-sm sm:text-lg md:text-xl max-w-2xl mx-auto opacity-80 leading-relaxed">
                    We’re a passionate team of builders, designers, and innovators creating high-impact platforms. Ready to do your best work?
                </p>
                <div className="mt-8 sm:mt-10 flex justify-center">
                    <a href="#openings" className="w-full sm:w-auto max-w-xs sm:max-w-none">
                        <CommonButton
                            text="Explore Open Positions"
                            icon={<Briefcase size={18} />}
                            iconPosition="right"
                            className="w-full sm:w-auto p-2 text-white justify-center bg-[#2F5E2E]"
                        />
                    </a>
                </div>
            </div>

            {/* WHY JOIN US / CULTURE SECTION */}
            <div className="py-12 sm:py-20 rounded-[2rem] sm:rounded-[3rem] mx-3 sm:mx-6 bg-[#FAFBF8] border border-slate-100">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center max-w-2xl mx-auto mb-10 sm:mb-16">
                        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold">
                            Why you'll love working here
                        </h2>
                        <p className="mt-3 text-xs sm:text-sm opacity-70 leading-relaxed">
                            We don't just offer jobs; we offer environments where your skills are valued, trusted, and amplified.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
                        {PERKS_DATA.map((perk, i) => (
                            <div
                                key={i}
                                className="bg-white p-6 sm:p-8 rounded-[1.5rem] border shadow-2xs transition transform hover:-translate-y-1 hover:shadow-xs"
                                style={{ borderColor: `${THEME.accent}4D` }}
                            >
                                <div className="p-3 rounded-2xl w-fit mb-4 flex items-center justify-center bg-[#F4F6F2]">
                                    {perk.icon}
                                </div>
                                <h3 className="text-base sm:text-lg font-bold mb-2">
                                    {perk.title}
                                </h3>
                                <p className="text-xs sm:text-sm opacity-75 leading-relaxed">
                                    {perk.desc}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* OPEN POSITIONS SECTION */}
            <div id="openings" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
                <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-8 sm:mb-12">
                    <div className="text-left">
                        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold">
                            Current Openings
                        </h2>
                        <p className="mt-2 text-xs sm:text-sm opacity-70">
                            Find your next career milestone below.
                        </p>
                    </div>

                    {/* Department Filters Tab Strip */}
                    <div className="flex flex-wrap lg:flex-nowrap gap-2 overflow-x-auto pb-1 scrollbar-none">
                        {departments.map((dept) => {
                            const isActive = selectedDept === dept;
                            return (
                                <button
                                    key={dept}
                                    onClick={() => setSelectedDept(dept)}
                                    className="px-4 py-2 rounded-xl text-xs font-bold border transition duration-150 whitespace-nowrap cursor-pointer shadow-3xs"
                                    style={{
                                        backgroundColor: isActive ? THEME.logoHeading : "#FFFFFF",
                                        color: isActive ? "#FFFFFF" : THEME.logoHeading,
                                        borderColor: isActive ? THEME.logoHeading : THEME.accent,
                                    }}
                                >
                                    {dept}
                                </button>
                            );
                        })}
                    </div>
                </div>

                {/* --- DYNAMIC LOADING INTERCEPT INDICATORS --- */}
                {isLoading && (
                    <div className="py-20 text-center text-xs font-semibold text-slate-400 italic flex items-center justify-center gap-2">
                        <Loader2 className="w-5 h-5 animate-spin text-[#2F5E2E]" />
                        <span>Fetching active position metrics from registry...</span>
                    </div>
                )}

                {isError && (
                    <div className="py-12 border border-rose-100 bg-rose-50/40 text-rose-700 font-bold rounded-2xl text-xs text-center">
                        Failed to fetch open positions from server registries. Please refresh workspace.
                    </div>
                )}

                {/* Job Cards Stack render pipeline view */}
                {!isLoading && !isError && (
                    <div className="space-y-4">
                        {filteredJobs.length > 0 ? (
                            filteredJobs.map((job) => (
                                <div
                                    key={job.id}
                                    className="group bg-white p-5 sm:p-6 rounded-2xl border border-slate-200/80 hover:border-[#6DBE45] transition flex flex-col md:flex-row md:items-center justify-between gap-4 text-left shadow-3xs hover:shadow-2xs"
                                >
                                    <div className="space-y-1.5">
                                        <h3 className="text-base sm:text-lg font-black text-slate-900 tracking-tight">
                                            {job.jobTitle}
                                        </h3>

                                        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs font-bold text-slate-400">
                                            <div className="flex items-center gap-1">
                                                <Briefcase size={13} className="text-[#2F5E2E]" />
                                                <span>{job.department}</span>
                                            </div>
                                            <span>•</span>
                                            <div className="flex items-center gap-1">
                                                <MapPin size={13} className="text-[#2F5E2E]" />
                                                <span>{job.location}</span>
                                            </div>
                                            <span>•</span>
                                            <div className="flex items-center gap-1">
                                                <Clock size={13} className="text-[#2F5E2E]" />
                                                <span>{job.employmentType}</span>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="w-full md:w-auto shrink-0">
                                        <CommonButton
                                            text="Apply Now"
                                            icon={<ChevronRight size={15} />}
                                            iconPosition="right"
                                            className="w-full md:w-auto px-5 h-10 text-white justify-center bg-[#2F5E2E] hover:bg-[#1f3f1e]"
                                            onClick={() => setActiveApplicationJob(job)}
                                        />
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className="text-center py-16 border border-dashed rounded-2xl border-slate-200 bg-slate-50/20 text-xs sm:text-sm text-slate-400 italic">
                                No active positions in this department right now. Check back soon!
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* --- WORKFLOW APPLICATION SUBMISSION MODAL LAYER --- */}
            {activeApplicationJob && (
                <JobApplicationModal 
                    job={activeApplicationJob}
                    onClose={() => setActiveApplicationJob(null)}
                />
            )}
        </div>
    );
};

export default CareersPage;